import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  Card,
  Tag,
  Space,
  Pagination,
  Row,
  Col,
  Typography,
  Image,
  message,
  Divider,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  LogoutOutlined,
} from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

export default function Supply() {
  // État pour l'authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm] = Form.useForm();

  // États pour les données
  const [provisionRequests, setProvisionRequests] = useState([]);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // États pour le modal
  const [modalVisible, setModalVisible] = useState(false);
  const [orgDetails, setOrgDetails] = useState(null);
  const [provisionDetails, setProvisionDetails] = useState(null);
  const [amountRequested, setAmountRequested] = useState(0);

  const requestsPerPage = 10;

  // Colonnes du tableau
  const columns = [
    {
      title: "Organisation",
      dataIndex: "organisationName",
      key: "organisationName",
    },
    {
      title: "Client",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `${amount} XOF`,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default";
        let text = status;

        switch (status) {
          case "pending":
            color = "warning";
            text = "En attente";
            break;
          case "completed":
            color = "success";
            text = "Confirmé";
            break;
          case "rejected":
            color = "error";
            text = "Refusé";
            break;
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Type d'approvisionnement",
      dataIndex: "provisionType",
      key: "provisionType",
    },
    {
      title: "Fournisseur",
      dataIndex: ["paymentProviderDetails", "label"],
      key: "paymentProvider",
    },
    {
      title: "Type de fournisseur",
      dataIndex: ["paymentProviderDetails", "type"],
      key: "paymentProviderType",
      render: (type) => <Text strong>{type}</Text>,
    },
    {
      title: "Numéro",
      dataIndex: ["paymentProviderDetails", "number"],
      key: "paymentProviderNumber",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type={record.status === "pending" ? "primary" : "default"}
          icon={<EyeOutlined />}
          onClick={() => {
            // ✅ FIX: Gérer les deux conventions de nommage
            const orgId = record.organisation_id || record.organisationId;
            handleOpenModal(orgId, record.id, record.amount);
          }}
        >
          {record.status === "completed"
            ? "Voir détails"
            : record.status === "pending"
              ? "Traiter"
              : record.status === "rejected"
                ? "Voir détails"
                : "Voir"}
        </Button>
      ),
    },
  ];

  // Authentification
  const handleLogin = (values) => {
    const { phone, password } = values;
    const validPhone = "88525497895626548";
    const validPassword = "trcbu,'__çùlfjfhee";

    if (phone === validPhone && password === validPassword) {
      setIsAuthenticated(true);
      message.success("Connexion réussie");
    } else {
      message.error("Numéro ou mot de passe incorrect");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    loginForm.resetFields();
    message.info("Déconnexion réussie");
  };

  // Récupération des données
  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Récupérer tous les fournisseurs de paiement
      const paymentRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payment_providers`,
      );
      const paymentProvidersData = await paymentRes.json();
      setPaymentProviders(paymentProvidersData);

      // 2. Récupérer toutes les demandes d'approvisionnement
      const provisionRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/provision_requests`,
      );
      const provisionData = await provisionRes.json();

      // Trier les demandes par date de création
      provisionData.sort(
        (a, b) =>
          new Date(b.createdAt)?.getTime() - new Date(a.createdAt)?.getTime(),
      );

      // 3. Transformer les données
      const transformedData = await Promise.all(
        provisionData.map(async (provision) => {
          const paymentProvider = paymentProvidersData.find(
            (provider) =>
              provider.reference === provision.paymentProviderReference,
          );

          // ✅ FIX: Gérer les deux conventions de nommage
          const orgId = provision.organisation_id || provision.organisationId;
          const userId = provision.user_id || provision.userId;

          const orgRes = await fetch(
            `${process.env.REACT_APP_API_URL}/api/organisations/${orgId}`,
          );
          const orgData = await orgRes.json();

          const userRes = await fetch(
            `${process.env.REACT_APP_API_URL}/api/users/${userId}`,
          );
          const userData = await userRes.json();

          return {
            ...provision,
            key: provision.id,
            userName: userData.fullname,
            organisationName: orgData.name,
            // ✅ Normaliser les champs pour faciliter l'accès
            organisationId: orgId,
            userId: userId,
            paymentProviderDetails: {
              reference: paymentProvider?.reference || "Non trouvé",
              label: paymentProvider?.label || "Non trouvé",
              type: paymentProvider?.type || "Non trouvé",
              number: paymentProvider?.number || "Non trouvé",
            },
          };
        }),
      );

      setProvisionRequests(transformedData);
    } catch (error) {
      console.error("Erreur lors de la récupération des données :", error);
      message.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  // ✅ CORRECTION : Récupération directe des détails de l'organisation
  const fetchOrganisationDetails = async (organisationId) => {
    try {
      // Récupérer directement l'organisation par son ID
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${organisationId}`,
      );

      if (!response.ok) {
        throw new Error("Organisation non trouvée");
      }

      const organisation = await response.json();
      setOrgDetails(organisation);
      return organisation;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des infos de l'organisation",
        error,
      );
      message.error("Impossible de charger les détails de l'organisation");
      return null;
    }
  };

  // ✅ CORRECTION : Récupération des détails de la provision
  const fetchProvisionDetails = async (provisionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/provision_requests/${provisionId}`,
      );

      if (!response.ok) {
        throw new Error("Demande d'approvisionnement non trouvée");
      }

      const provisionData = await response.json();

      // Enrichir avec les détails du payment provider
      const paymentProvider = paymentProviders.find(
        (provider) =>
          provider.reference === provisionData.paymentProviderReference,
      );

      const enrichedProvision = {
        ...provisionData,
        paymentProviderDetails: paymentProvider || {},
        paymentProviderLabel: paymentProvider?.label || "Non trouvé",
      };

      setProvisionDetails(enrichedProvision);
      return enrichedProvision;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails de la provision",
        error,
      );
      message.error("Impossible de charger les détails de la demande");
      return null;
    }
  };

  // ✅ CORRECTION : Ouverture du modal avec chargement asynchrone
  const handleOpenModal = async (organisationId, provisionId, amount) => {
    setModalVisible(true);
    setAmountRequested(Number(amount));

    // Charger les détails en parallèle
    await Promise.all([
      fetchOrganisationDetails(organisationId),
      fetchProvisionDetails(provisionId),
    ]);
  };

  // ✅ CORRECTION : Mise à jour du wallet avec vérifications
  const updateWallet = async () => {
    // Vérification des données nécessaires
    if (!orgDetails) {
      message.error("Les détails de l'organisation ne sont pas chargés");
      console.error("orgDetails est null");
      return;
    }

    if (!provisionDetails) {
      message.error("Les détails de la provision ne sont pas chargés");
      console.error("provisionDetails est null");
      return;
    }

    // ✅ FIX: L'API retourne organisation_id (snake_case) pas organisationId (camelCase)
    const organisationId =
      orgDetails.organisation_id || orgDetails.organisationId;

    if (!organisationId) {
      message.error("ID d'organisation manquant");
      console.error("organisationId manquant dans orgDetails:", orgDetails);
      return;
    }

    try {
      // Calculer le nouveau solde
      const currentBalance = Number(orgDetails.wallet?.balance || 0);
      const newBalance = currentBalance + amountRequested;

      console.log("Mise à jour du wallet:", {
        organisationId: organisationId,
        currentBalance,
        amountRequested,
        newBalance,
      });

      // Mise à jour du wallet
      const walletRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organisations/${organisationId}/wallet`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            balance: newBalance,
          }),
        },
      );

      if (!walletRes.ok) {
        const errorData = await walletRes.json();
        throw new Error(
          errorData.error || "Erreur lors de la mise à jour du wallet",
        );
      }

      // Mise à jour du statut de la provision
      const provisionRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/provision_requests/${provisionDetails.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "completed" }),
        },
      );

      if (!provisionRes.ok) {
        const errorData = await provisionRes.json();
        throw new Error(
          errorData.error || "Erreur lors de la mise à jour de la provision",
        );
      }

      message.success("Demande traitée avec succès");
      await fetchData(); // Recharger les données
      setModalVisible(false);

      // Réinitialiser les états
      setOrgDetails(null);
      setProvisionDetails(null);
      setAmountRequested(0);
    } catch (error) {
      console.error("Erreur :", error);
      message.error("Échec de la mise à jour : " + error.message);
    }
  };

  // ✅ CORRECTION : Rejet de la provision avec vérifications
  const rejectProvisionRequest = async () => {
    if (!provisionDetails) {
      message.error("Les détails de la provision ne sont pas chargés");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/provision_requests/${provisionDetails.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "rejected" }),
        },
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.error || "Erreur lors du rejet de la demande",
        );
      }

      message.success("Demande rejetée avec succès");
      await fetchData();
      setModalVisible(false);

      // Réinitialiser les états
      setOrgDetails(null);
      setProvisionDetails(null);
      setAmountRequested(0);
    } catch (error) {
      console.error("Erreur lors du rejet :", error);
      message.error("Échec du rejet : " + error.message);
    }
  };

  // Filtrage et pagination
  const filteredRequests = provisionRequests.filter((request) => {
    const matchesSearch =
      request.organisationName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      request.userName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest,
  );

  // Interface de connexion
  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Card
          title="Connexion Administrateur"
          style={{ width: 400, boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
        >
          <Form form={loginForm} onFinish={handleLogin} layout="vertical">
            <Form.Item
              label="Numéro de téléphone"
              name="phone"
              rules={[
                {
                  required: true,
                  message: "Veuillez entrer votre numéro de téléphone",
                },
              ]}
            >
              <Input placeholder="Entrez votre numéro" />
            </Form.Item>

            <Form.Item
              label="Mot de passe"
              name="password"
              rules={[
                {
                  required: true,
                  message: "Veuillez entrer votre mot de passe",
                },
              ]}
            >
              <Input.Password placeholder="Entrez votre mot de passe" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Se connecter
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  // Interface principale
  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            Gestion des demandes d'approvisionnement
          </Title>
        }
      >
        <div style={{ marginBottom: "16px" }}>
          <Row gutter={16}>
            <Col span={8}>
              <Search
                placeholder="Rechercher par organisation ou client"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={6}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: "100%" }}
                placeholder="Filtrer par statut"
              >
                <Option value="all">Toutes les demandes</Option>
                <Option value="pending">En attente</Option>
                <Option value="completed">Traitées</Option>
                <Option value="rejected">Refusées</Option>
              </Select>
            </Col>
            <Col span={4} offset={6}>
              <Button
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                block
              >
                Déconnexion
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={currentRequests}
          loading={loading}
          pagination={false}
          scroll={{ x: true }}
        />

        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <Pagination
            current={currentPage}
            total={filteredRequests.length}
            pageSize={requestsPerPage}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} sur ${total} demandes`
            }
          />
        </div>
      </Card>

      {/* Modal des détails */}
      <Modal
        title={`Demande d'approvisionnement ${orgDetails ? `de ${orgDetails.name}` : ""}`}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setOrgDetails(null);
          setProvisionDetails(null);
          setAmountRequested(0);
        }}
        width={800}
        footer={
          provisionDetails?.status === "pending"
            ? [
                <Button
                  key="reject"
                  type="primary"
                  danger
                  onClick={rejectProvisionRequest}
                  disabled={!orgDetails || !provisionDetails}
                >
                  Refuser
                </Button>,
                <Button key="cancel" onClick={() => setModalVisible(false)}>
                  Annuler
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  onClick={updateWallet}
                  disabled={!orgDetails || !provisionDetails}
                >
                  Confirmer
                </Button>,
              ]
            : [
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setModalVisible(false)}
                >
                  Fermer
                </Button>,
              ]
        }
      >
        {!orgDetails || !provisionDetails ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Text>Chargement des détails...</Text>
          </div>
        ) : (
          <>
            <Title level={4}>Détails de la demande</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text strong>ID : </Text>
                    <Text>{provisionDetails.id}</Text>
                  </div>
                  <div>
                    <Text strong>Montant à approvisionner : </Text>
                    <Text>{provisionDetails.amount} XOF</Text>
                  </div>
                  <div>
                    <Text strong>Méthode de paiement : </Text>
                    <Text>
                      {provisionDetails.paymentProviderDetails?.label}
                    </Text>
                  </div>
                  <div>
                    <Text strong>Numéro du compte : </Text>
                    <Text>
                      {provisionDetails.paymentProviderDetails?.number}
                    </Text>
                  </div>
                  <div>
                    <Text strong>Preuve du paiement : </Text>
                    {provisionDetails.documentUrl && (
                      <div style={{ marginTop: "8px" }}>
                        <Image
                          width={100}
                          height={100}
                          src={provisionDetails.documentUrl}
                          alt="Preuve de paiement"
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    )}
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text strong>Type d'approvisionnement : </Text>
                    <Text>{provisionDetails.provisionType}</Text>
                  </div>
                  <div>
                    <Text strong>Nom de l'organisation : </Text>
                    <Text>{provisionDetails.organisationName}</Text>
                  </div>
                  <div>
                    <Text strong>Opérateur : </Text>
                    <Text>{provisionDetails.paymentProviderLabel}</Text>
                  </div>
                  <div>
                    <Text strong>Statut : </Text>
                    <Tag
                      color={
                        provisionDetails.status === "pending"
                          ? "warning"
                          : provisionDetails.status === "completed"
                            ? "success"
                            : provisionDetails.status === "rejected"
                              ? "error"
                              : "default"
                      }
                    >
                      {provisionDetails.status === "pending"
                        ? "En attente"
                        : provisionDetails.status === "completed"
                          ? "Confirmé"
                          : provisionDetails.status === "rejected"
                            ? "Refusé"
                            : provisionDetails.status}
                    </Tag>
                  </div>
                  <div>
                    <Text strong>Date et heure : </Text>
                    <Text>
                      {provisionDetails.createdAt
                        ? new Date(provisionDetails.createdAt).toLocaleString()
                        : ""}
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>

            <Divider />

            <Title level={4}>Confirmer la demande d'approvisionnement</Title>
            <Space direction="vertical" style={{ width: "100%" }}>
              {provisionDetails.status === "completed" && (
                <>
                  <Text type="success" strong>
                    Demande Traitée
                  </Text>
                  <Input
                    addonBefore="Montant demandé"
                    value={`${amountRequested} XOF`}
                    readOnly
                  />
                  <Input
                    addonBefore="Solde actuel"
                    value={`${orgDetails.wallet?.balance || 0} XOF`}
                    readOnly
                  />
                </>
              )}

              {provisionDetails.status === "rejected" && (
                <>
                  <Text type="danger" strong>
                    Demande refusée
                  </Text>
                  <Input
                    addonBefore="Solde actuel"
                    value={`${orgDetails.wallet?.balance || 0} XOF`}
                    readOnly
                  />
                  <Input
                    addonBefore="Montant demandé"
                    value={`${amountRequested} XOF`}
                    readOnly
                  />
                </>
              )}

              {provisionDetails.status === "pending" && (
                <>
                  <Text type="warning" strong>
                    Demande en attente
                  </Text>
                  <Input
                    addonBefore="Solde actuel"
                    value={`${orgDetails.wallet?.balance || 0} XOF`}
                    readOnly
                  />
                  <Input
                    addonBefore="Montant demandé"
                    value={`${amountRequested} XOF`}
                    readOnly
                  />
                  <Input
                    addonBefore="Total après approvisionnement"
                    value={`${
                      (orgDetails.wallet?.balance || 0) + amountRequested
                    } XOF`}
                    readOnly
                  />
                </>
              )}
            </Space>
          </>
        )}
      </Modal>
    </div>
  );
}
